import { Download, Eye, Paperclip, Trash2, Upload } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import type { Attachment } from "@/hooks/use-attachments";
import {
  useDeletePayAppAttachment,
  usePayAppAttachments,
  useUploadPayAppAttachment,
} from "@/hooks/use-attachments";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isPreviewable(mimeType: string | null): "image" | "pdf" | null {
  if (!mimeType) return null;
  if (mimeType.startsWith("image/")) return "image";
  if (mimeType === "application/pdf") return "pdf";
  return null;
}

interface PreviewState {
  attachment: Attachment;
  blobUrl: string | null;
  loading: boolean;
  error: string | null;
}

interface AttachmentSectionProps {
  payAppType: "sub" | "owner";
  payAppId: string;
  readOnly?: boolean;
}

export function AttachmentSection({ payAppType, payAppId, readOnly }: AttachmentSectionProps) {
  const { data: attachments } = usePayAppAttachments(payAppType, payAppId);
  const uploadMutation = useUploadPayAppAttachment(payAppType, payAppId);
  const deleteMutation = useDeletePayAppAttachment(payAppType, payAppId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<PreviewState | null>(null);

  const items = attachments ?? [];

  useEffect(() => {
    return () => {
      if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    };
  }, [preview?.blobUrl]);

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      uploadMutation.mutate(file);
      e.target.value = "";
    }
  }

  const downloadUrl = (a: Attachment) =>
    `/api/pay-applications/${payAppType === "sub" ? "subcontract" : "owner"}/${payAppId}/attachments/${a.id}`;

  async function openPreview(attachment: Attachment) {
    setPreview({ attachment, blobUrl: null, loading: true, error: null });
    try {
      const res = await fetch(downloadUrl(attachment));
      if (!res.ok) throw new Error(`Failed to load file (${res.status})`);
      const blob = await res.blob();
      const typedBlob =
        attachment.mimeType && blob.type !== attachment.mimeType
          ? new Blob([blob], { type: attachment.mimeType })
          : blob;
      const url = URL.createObjectURL(typedBlob);
      setPreview({ attachment, blobUrl: url, loading: false, error: null });
    } catch (err) {
      setPreview({
        attachment,
        blobUrl: null,
        loading: false,
        error: err instanceof Error ? err.message : "Failed to load preview",
      });
    }
  }

  function closePreview() {
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base">
          <Paperclip className="mr-2 inline h-4 w-4" />
          Attachments
        </CardTitle>
        {!readOnly && (
          <>
            <input type="file" ref={fileInputRef} className="hidden" onChange={handleFileChange} />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploadMutation.isPending ? "Uploading..." : "Upload"}
            </Button>
          </>
        )}
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No attachments.</p>
        ) : (
          <div className="space-y-2">
            {items.map((a) => {
              const canPreview = isPreviewable(a.mimeType) !== null;
              return (
                <div
                  key={a.id}
                  className="flex items-center gap-3 rounded-md border px-3 py-2 text-sm"
                >
                  <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{a.fileName}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatFileSize(a.fileSize)}
                      {a.uploadedBy ? ` · ${a.uploadedBy}` : ""}
                      {a.uploadedDate ? ` · ${a.uploadedDate}` : ""}
                    </p>
                  </div>
                  {canPreview && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => openPreview(a)}
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                  )}
                  <a href={downloadUrl(a)} download className="shrink-0">
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <Download className="h-4 w-4" />
                    </Button>
                  </a>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      onClick={() => deleteMutation.mutate(a.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={preview !== null} onOpenChange={(open) => !open && closePreview()}>
        <DialogContent className="w-[50vw] max-w-[50vw] sm:max-w-[50vw] h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              <span className="truncate">{preview?.attachment.fileName}</span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden">
            {preview?.loading && <Skeleton className="h-full w-full" />}
            {preview?.error && (
              <div className="flex h-full flex-col items-center justify-center gap-3">
                <p className="text-sm text-destructive">{preview.error}</p>
                <a href={downloadUrl(preview.attachment)} download>
                  <Button variant="outline" size="sm">
                    <Download className="mr-2 h-4 w-4" /> Download instead
                  </Button>
                </a>
              </div>
            )}
            {preview?.blobUrl &&
              (isPreviewable(preview.attachment.mimeType) === "image" ? (
                <div className="flex h-full items-center justify-center overflow-auto bg-muted/30">
                  <img
                    src={preview.blobUrl}
                    alt={preview.attachment.fileName}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <iframe
                  title={preview.attachment.fileName}
                  src={preview.blobUrl}
                  className="h-full w-full"
                />
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
